import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AuthenticationService } from "./authentication.service";
import { environment } from "environments/environment";
import { User } from "@core/models/user";
import { firstValueFrom, Observable } from "rxjs";
import { ObjectUtils } from "@core/utils/object-utils.component";
import { ResetPasswordModel } from "@core/models/resetPasswordModel";
import { SendResetPasswordModel } from "@core/models/sendResetPasswordModel";
import { Pagination } from "@core/models/generic/pagination";
import { StringUtils } from "@core/utils/string-utils.component";
import { RoleType } from "@core/enums/roles.enum";

@Injectable({ providedIn: 'root' })
export class UserService {

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService
  ) { }

  list(
    companyId?: string,
    page: number | null = null,
    pageSize: number | null = null,
    filterText: string | null = null,
    avoidAdminOfCompany?: boolean
  ): Observable<Pagination<User>> {
    let url = `${environment.PackagingUrl}/User/List?companyId=${companyId}&avoidAdminOfCompany=${avoidAdminOfCompany}`;

    if (page !== null && pageSize !== null)
      url += `&CurrentPage=${page}&PageSize=${pageSize}`;

    if (!StringUtils.isNullOrWhiteSpace(filterText ?? ''))
      url += `&FilterText=${filterText}`;

    return this.http.get<Pagination<User>>(url);
  }

  update(user: User): Observable<User> {
    return this.http.put<User>(`${environment.PackagingUrl}/User`, user);
  }

  sendResetUserPasswordEmail = (userEmail: string): Observable<boolean> => {
    const postBody: SendResetPasswordModel = { userEmail: userEmail };
    return this.http.post<boolean>(`${environment.PackagingUrl}/Token/SendResetPasswordToken`, postBody);
  }

  resetUserPassword = (resetPasswordToken: string, oldPW: string | null = null, newPW: string, confirmPW: string): Observable<boolean> => {
    const postBody: ResetPasswordModel = { resetPasswordToken, oldPW, newPW, confirmPW };
    return this.http.post<boolean>(`${environment.PackagingUrl}/Token/ChangePassword`, postBody);
  }

  resetUserPasswordAsync = async (resetPasswordToken: string, oldPW: string | null, newPW: string, confirmPW: string): Promise<boolean> => {
    return firstValueFrom(this.resetUserPassword(resetPasswordToken, oldPW, newPW, confirmPW));
  }

  refreshUserPassword = (resetPasswordToken: string, oldPW: string, newPW: string, confirmPW: string): Observable<boolean> => {
    const postBody: ResetPasswordModel = { resetPasswordToken, oldPW, newPW, confirmPW };
    return this.http.post<boolean>(`${environment.PackagingUrl}/Token/ChangePassword`, postBody);
  }

  refreshUserPasswordAsync = async (resetPasswordToken: string, oldPW: string, newPW: string, confirmPW: string): Promise<boolean> => {
    return firstValueFrom(this.refreshUserPassword(resetPasswordToken, oldPW, newPW, confirmPW));
  }

  checkCurrentUserIsAdmin = (): boolean => {
    return this.userHasRole(this.authenticationService?.currentUser, 'gip_admin');
  }

  checkCurrentUserIsViewer(): boolean {
    return this.userHasRole(this.authenticationService.currentUser, RoleType[RoleType.VM]);
  }

  private userHasRole = (user: any, roleId: string): boolean => {
    if (ObjectUtils.isNullOrUndefined(user)) {
      return false;
    }
    return user?.roles?.some((role: any) =>
      (role?.roleId ?? role)?.toLowerCase() === roleId?.toLowerCase()
    ) ?? false;
  }
}
